import FormCheckbox from '@ui/checkbox/FormCheckbox';
import { findLowestAnchorRoom } from './recommmendation/overlay/PriceRecommendationDrawerContents.utilities';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import CalendarMonth from '@app/state/calendar/CalendarMonth';
import Utilities from '@util/Utilities';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, ExclamationTriangleIcon, PaperAirplaneIcon } from '@heroicons/react/16/solid';
import FormButton from '@ui/button/FormButton';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import Dialogue from '@ui/dialog/Dialogue';
import useDialogue from '@ui/dialog/DialogueRef';
import Spinner from '@ui/spinner/Spinner';
import SubmitPricingResultOutput from '../../../../../../../../model/pricing-automation/output/SubmitPricingResultOutput';
import Message from '@app/component/message/Message';
import SubmitMonthlyPricingResultOutputState
	from '../../../../../../../../model/pricing-automation/output/SubmitMonthlyPricingResultOutputState';
import { useTranslation } from '@app/component/translation/Translation';
import { MarketStateGraphQLQueryKeys } from '@app/model/market_state/service/MarketStateGraphQLQueryKeys';
import FormButtonStyleState from '@ui/button/FormButtonStyleState';
import PriceSubmissionBlockedItems from './PriceSubmissionBlockedItems';
import { useNavigate } from 'react-router-dom';
import { usePricingRights } from '@util/PricingRightsHook';
import { useMarketStateTools } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import { findSubmittableRecommendationStates } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/MarketStateSnapshotDateBasedTableRowFragment.utilities';
import { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';

type SubmittableDay = {
	statDate: string,
	date: Date,
	states: PriceRecommendationState[],
};

// The percent of one recommendation, as the table shows it.
const formatChangeRate = (rate: number) => `${rate > 0 ? '+' : ''}${Utilities.roundDouble(rate * 100, 0)}%`;


const SubmitMonthlyPricesButton: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	calendarMonth: CalendarMonth,
	label?: string,
	state?: FormButtonStyleState,
	size?: 'normal' | 'small',
	disabled?: boolean,
	extraClassName?: string
}> = ({ companyHotel, calendarMonth, label = 'Review & Submit', state = 'primary', size = 'normal', disabled = false, extraClassName }) => {

	const dialogue = useDialogue();

	return <>
		<FormButton state={state}
			size={size}
			disabled={disabled || calendarMonth.isInPast()}
			onClick={() => dialogue.open()}
			extraClassName={Utilities.classNames(size === 'small' ? 'relative' : 'py-1.5 relative', extraClassName)}
			icon={<PaperAirplaneIcon
				className={size === 'small' ? 'h-3.5 w-3.5' : 'w-4 h-4 mr-2 mt-0.5'}
				aria-hidden="true" />}>
			{label}
		</FormButton>
		<Dialogue ref={dialogue.ref} title="Review & Submit Prices" noActions>
			<SubmitMonthlyPricesDialogueContents companyHotel={companyHotel} calendarMonth={calendarMonth}
				onClose={() => dialogue.close()} />
		</Dialogue>
	</>;
};

const SubmitMonthlyPricesDialogueContents: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	calendarMonth: CalendarMonth,
	onClose: () => void
}> = ({ companyHotel, calendarMonth, onClose }) => {
	const pending = useRef(false);
	const [submittedDateCount, setSubmittedDateCount] = useState(0);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const [result, setResult] = useState<SubmitPricingResultOutput | null>(null);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const tools = useMarketStateTools();
	const { canViewRecommendations } = usePricingRights(companyHotel);
	const [showDates, setShowDates] = useState(false);
	const [untickedStatDates, setUntickedStatDates] = useState<ReadonlySet<string>>(new Set());
	// The day list: the dates of the month, from today, with a valid recommendation that has no
	// successful submission. This is the rule of the server for the monthly push, computed from the page data.
	const today = Utilities.formatISODate(new Date());
	const days = useMemo<SubmittableDay[]>(() => tools.marketStateDailySnapshotStates
		.filter(dailySnapshotState => dailySnapshotState.calendarDay.isCurrentMonth && dailySnapshotState.calendarDay.display >= today)
		.map(dailySnapshotState => ({
			statDate: dailySnapshotState.calendarDay.display,
			date: dailySnapshotState.calendarDay.date,
			states: findSubmittableRecommendationStates(dailySnapshotState, companyHotel, canViewRecommendations, today),
		}))
		.filter(day => day.states.length > 0), [canViewRecommendations, companyHotel, tools.marketStateDailySnapshotStates, today]);
	const tickedDays = days.filter(day => !untickedStatDates.has(day.statDate));
	const tickedRecommendationIds = tickedDays.flatMap(day => day.states.map(state => state.id));
	const toggleDay = (statDate: string, ticked: boolean) => setUntickedStatDates(current => {
		const next = new Set(current);
		if (ticked) next.delete(statDate); else next.add(statDate);
		return next;
	});
	const toggleAllDays = () => setUntickedStatDates(tickedDays.length === days.length ? new Set(days.map(day => day.statDate)) : new Set());
	const selectBlockedDate = (statDate: string) => {
		onClose();
		navigate('/secured/rateshopper/rates/market/calendar', { state: { selectedStatDate: statDate } });
	};

	useEffect(() => {
		setResult(null);
	}, []);

	const queryClient = useQueryClient();
	// Decision 2: the ticked dates go through the selective mutation, always. The monthly mutation stays in the API.
	const submitPricingMutation = useMutation({
		mutationFn: PricingAutomationService.submitSelectivePricing,
		onMutate: () => {
			setResult(null);
			setIsSubmitted(true);
			setIsProcessing(true);
		},
		onSuccess: (result) => {
			setResult(result);
			void queryClient.invalidateQueries({
				queryKey: MarketStateGraphQLQueryKeys.all,
			});
		},
		onSettled: () => { pending.current = false; setIsProcessing(false); },
	});

	if (!isSubmitted && !isProcessing) {
		const allTicked = tickedDays.length === days.length;
		const noneTicked = tickedDays.length === 0;
		return <div className="flex flex-col">
			<div className="flex items-start gap-3 px-1 py-2">
				<div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-md bg-blue-50">
					<PaperAirplaneIcon className="w-5 h-5 text-blue-600" aria-hidden="true" />
				</div>
				<div className="flex-1 pt-1">
					<p className="text-sm font-semibold text-gray-900">
						{calendarMonth.display} pricing
					</p>
					<p className="mt-1 text-sm text-gray-600 leading-relaxed">
						Submit the monthly prices for this property.
					</p>
				</div>
			</div>
			<div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
				<div className="flex gap-2">
					<ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-none text-amber-600" aria-hidden="true" />
					<p className="text-xs leading-5 text-amber-800">
						This will push prices for the selected dates and cannot be undone.
					</p>
				</div>
			</div>
			<div className="mt-4 flex items-center justify-between text-sm">
				<span className="text-gray-700">
					<span className="font-semibold text-gray-900">
						{allTicked ? days.length : `${tickedDays.length} of ${days.length}`} {days.length === 1 ? 'date' : 'dates'}
					</span>
					{allTicked ? ' with a recommendation' : ' selected'}

				</span>
				{days.length > 0 && <FormButton state="text"
																		className="inline-flex items-center gap-x-1 text-sm font-semibold text-blue-600 hover:text-blue-500"
																		onClick={() => setShowDates(value => !value)}>
					{showDates ? <ChevronUpIcon className="h-4 w-4" aria-hidden="true" /> : <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />}
					{showDates ? 'Hide dates' : 'Show dates'}
				</FormButton>}
			</div>
			{showDates && <div className="mt-2 overflow-hidden rounded-md bg-white ring-1 ring-gray-200">
				<div className="flex items-center gap-x-2 bg-gray-50 px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
					<FormCheckbox name="selectAllReviewDates" label="Select all dates" labelExtraClassName="sr-only"
						checked={allTicked} indeterminate={!allTicked && !noneTicked} onChange={toggleAllDays} />
					<span className="w-28">Date</span>
					<span className="flex-1">Recommendation per room type</span>
				</div>
				<div className="max-h-80 divide-y divide-gray-100 overflow-y-auto">
					{days.map(day => {
						const ticked = !untickedStatDates.has(day.statDate);
						return <div key={day.statDate}
							onClick={event => {
								if (!(event.target instanceof Element) || event.target.closest('input, label')) return;
								toggleDay(day.statDate, !ticked);
							}}
													className={Utilities.classNames(
														'flex cursor-pointer items-center gap-x-2 px-2 py-1.5 text-xs hover:bg-gray-50',
														ticked ? 'text-gray-900' : 'text-gray-400')}>
							<FormCheckbox name={`reviewDate-${day.statDate}`} label={Utilities.formatDate(day.date, 'ddd DD MMM')}
								labelExtraClassName="w-28 !text-xs !font-medium !text-inherit"
								checked={ticked} onChange={event => toggleDay(day.statDate, event.target.checked)} />
							<span className="flex flex-1 flex-wrap gap-x-4">
								{day.states.map(state => {
									const anchor = findLowestAnchorRoom(state.minimumBasePriceRoomStates);
									return <span key={state.id}>
										<span className="text-gray-400">{companyHotel.findRoomMappingTypeName(state.roomMappingType)}</span>
										{' '}{anchor !== undefined ? Utilities.formatAmount(anchor.finalPrice, companyHotel.settings.main.currency, 0) : ''}
										{' '}{formatChangeRate(state.effectiveChangeRate)}
									</span>;
								})}
							</span>
						</div>;
					})}
				</div>
			</div>}
			<div className="mt-6 flex justify-end gap-2 border-t border-gray-200 pt-4">
				<FormButton state="white" onClick={onClose}>
					Cancel
				</FormButton>
				<FormButton state="primary"
							disabled={noneTicked}
							onClick={() => {
					if (pending.current || tickedRecommendationIds.length === 0) return;
					pending.current = true;
					setSubmittedDateCount(tickedDays.length);
					submitPricingMutation.mutate({
											companyHotelId: companyHotel.id,
											input: { recommendationIds: tickedRecommendationIds },
										}); }}>
					Submit {tickedDays.length} {tickedDays.length === 1 ? 'date' : 'dates'}
				</FormButton>
			</div>
		</div>;
	}

	if (isSubmitted && isProcessing) {
		return <div className="flex flex-col items-center justify-center gap-3 py-10">
			<Spinner width={10} height={10} className="w-10 h-10 text-blue-600 fill-blue-100 animate-spin" />
			<p className="text-sm font-medium text-gray-700">
				Submitting the prices of {submittedDateCount} {submittedDateCount === 1 ? 'date' : 'dates'} of{' '}
				<span className="font-semibold text-gray-900">
					{calendarMonth.display}
				</span>
				...
			</p>
			<p className="text-xs text-gray-500">
				Please wait while we push the prices. This may take a moment.
			</p>
		</div>;
	}

	if (result !== null) {
		const isSucceeded = result.state === SubmitMonthlyPricingResultOutputState.SUCCESS;
		const submissionError = result.errorCode ?? result.errorMessage;

		return <div className="flex flex-col">
			<div className="flex items-start gap-3 px-1 py-2">
				<div className={Utilities.classNames(
					'flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full',
					isSucceeded ? 'bg-green-100' : 'bg-red-100',
				)}>
					{isSucceeded
						? <CheckCircleIcon className="w-6 h-6 text-green-600" aria-hidden="true" />
						: <ExclamationTriangleIcon className="w-6 h-6 text-red-600" aria-hidden="true" />}
				</div>
				<div className="flex-1 pt-1">
					<p className="text-sm font-semibold text-gray-900">
						{isSucceeded
							? 'Prices submitted successfully'
							: 'Price submission failed'}
					</p>
					<p className="mt-1 text-xs text-gray-500">
						{submittedDateCount} {submittedDateCount === 1 ? 'date' : 'dates'} of{' '}
						<span className="font-medium text-gray-700">
							{calendarMonth.display}
						</span>
					</p>
				</div>
			</div>

			{!isSucceeded && result.blockedItems.length > 0 &&
				<PriceSubmissionBlockedItems
					items={result.blockedItems}
					currency={companyHotel.settings.main.currency}
					onDateSelect={selectBlockedDate} />}

			{!isSucceeded && result.blockedItems.length === 0 && submissionError !== undefined && submissionError !== '' && <Message state="error">
				{t(submissionError, ...(result.errorParams ?? []))}
			</Message>}

			<div className="mt-6 flex justify-end gap-2 border-t border-gray-200 pt-4">
				<FormButton state="primary" onClick={onClose}>
					Close
				</FormButton>
			</div>
		</div>;
	}

	return <>
		<Message state="error">An error occurred while submitting the monthly prices.</Message>
		<div className="mt-6 flex justify-end gap-2 border-t border-gray-200 pt-4">
			<FormButton state="white" onClick={onClose}>
				Close
			</FormButton>
		</div>
	</>;

};

export default SubmitMonthlyPricesButton;
